// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import XngZone from "@stoneage/App/Components/Common/XngZone";
import CSSTransition from "react-transition-group/CSSTransition";
import useScrollDirection from "@stoneage/App/Hooks/useScrollDirection";
import { useEffect, useRef } from "react";
import HeaderClickable, { HeaderClickableStyles } from "@stoneage/App/Components/Header/HeaderClickable";
import translate from "@insite/client-framework/Translate";
import MainNavigationButton from "@stoneage/App/Components/Header/MainNavigationButton";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    // XNG-Changes
    innerContainer?: InjectableCss;
    actionsWrapper?: InjectableCss;
    searchMenuMobileWrapper?: InjectableCss;
    searchHeaderClickable?: HeaderClickableStyles;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 45px;
            margin: 0 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 78px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 42px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    linkListHidden: {
        below: "lg",
    },
};

const LogoLinksSearch: React.FC<PageProps> = ({ id }) => {
    const { visible, toggle } = useScrollDirection();
    const { isEditing } = useShellContext();
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (searchRef.current) {
            const h = searchRef.current.scrollHeight;
            searchRef.current.style.setProperty("--search-height", `${h}px`);
        }
    }, []);

    return (
        <StyledWrapper {...logoLinksSearchStyles.container} data-test-selector="LogoLinksSearch">
            <StyledWrapper {...logoLinksSearchStyles.innerContainer} data-test-selector="LogoLinksSearch">
                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                    <MainNavigationButton />
                    <XngZone contentId={id} zoneName="LogoZone" hint="Logo" />
                </StyledWrapper>
                <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                    <HeaderClickable
                        data-test-selector="mobile-search"
                        aria-label={translate("ToggleSearch")}
                        extendedStyles={logoLinksSearchStyles.searchHeaderClickable}
                        showIcon={true}
                        onClick={toggle}
                    />
                    <XngZone contentId={id} zoneName="Actions" />
                </StyledWrapper>
            </StyledWrapper>
            <CSSTransition nodeRef={searchRef} in={visible} timeout={300} classNames="search" unmountOnExit={false}>
                <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper} {...{ visible }} ref={searchRef}>
                    <XngZone contentId={id} zoneName="SearchZone" hint="Search" />
                </StyledWrapper>
            </CSSTransition>
            {isEditing && <XngZone contentId={id} zoneName="MainNavigation" hint="Depricated" />}
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: LogoLinksSearch,
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
